import { useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { FOLDER_LINK_PAGE, type MeResponse, type View } from '@pathonline/shared';
import { useEffect, useRef, useState } from 'react';
import { SIGN_OUT_URL, sendJson, useMe, useUnread } from './api.ts';
import { oldDest } from './dest.ts';
import { rolesText } from './format.ts';
import { Landing } from './pages/Landing.tsx';
import { NoAccess } from './pages/NoAccess.tsx';
import { SessionBar } from './SessionBar.tsx';
import { ThemeToggle } from './theme.tsx';
import { NewVersionBar, SiteVersionLabel } from './version.tsx';

type Member = Extract<MeResponse, { member: true }>;
type TabRoute =
  | '/management' | '/topics' | '/network' | '/gantt' | '/outputs' | '/comments' | '/strategy' | '/admin' | '/users'
  | '/meetings' | '/apply' | '/inbox' | '/suggestions' | '/my-applications' | '/suggest' | '/guides';

/**
 * A tab. `icon` is hidden from screen readers; `also`: more pages it is the active tab for.
 * `bar`: its page has the old dark-blue header bar above the tabs: the label, and the hint on the right.
 * `visitor`: 'public' anyone may open its page, signed in or not; 'applicant' an unlisted visitor may,
 * once applications are open (they are enrolled as an applicant).
 * `visitorTitle`: the page name in the visitor header, if not the label.
 */
type Tab = {
  to: TabRoute;
  icon: string;
  label: string;
  also?: string[];
  bar?: { hint?: string };
  visitor?: 'public' | 'applicant';
  visitorTitle?: string;
};

/** The tab of each view. The tab row shows them as the API lists `nav`: in the old nav bar's order. */
const VIEW_TAB: Record<Exclude<View, 'home'>, Tab> = {
  management: { to: '/management', icon: '🗂', label: 'Management', bar: {} },
  topics: { to: '/topics', icon: '🌳', label: 'Topics', bar: {} },
  network: { to: '/network', icon: '🕸', label: 'Network', bar: {} },
  gantt: { to: '/gantt', icon: '📅', label: 'Gantt', bar: { hint: 'Tasks grouped by grant · click a row to edit dates & people' } },
  outputs: { to: '/outputs', icon: '📄', label: 'Outputs' },
  comments: { to: '/comments', icon: '💬', label: 'Comments', bar: { hint: 'Every thread in one queue, newest first · hover a task to preview it, click to open the thread' } },
  strategy: { to: '/strategy', icon: '🧭', label: 'Strategy', bar: { hint: 'Strategy views on the shared content · drop files anywhere to add to it' } },
  admin: { to: '/admin', icon: '⚙', label: 'Admin', bar: {} },
  users: { to: '/users', icon: '👥', label: 'Users', also: ['/persons', '/programmes'] },
};

/** The tabs after the views. `show`: whether this member gets it. */
const MORE_TABS: (Tab & { show: (m: Member) => boolean })[] = [
  { to: '/meetings', icon: '🗓', label: 'Meetings', show: (m) => m.nav.includes('gantt') },
  { to: '/apply', icon: '🎓', label: 'Open topics', visitor: 'public', visitorTitle: 'Open Research Topics', show: (m) => m.applyOpen },
  { to: '/inbox', icon: '📥', label: 'Applications', show: (m) => m.applyOpen && m.inbox },
  { to: '/suggestions', icon: '📋', label: 'Topic suggestions', show: (m) => m.reviewer },
  { to: '/my-applications', icon: '📄', label: 'My applications', visitor: 'applicant', show: (m) => m.applyOpen && m.group === 'applicant' },
  { to: '/suggest', icon: '💡', label: 'Suggest a topic', visitor: 'applicant', show: (m) => m.applyOpen && m.suggest },
  { to: '/guides', icon: '❓', label: 'Guides', visitor: 'public', show: () => true },
];

/** Every tab: Layout finds the one of the page shown. */
const ALL_TABS: Tab[] = [...Object.values(VIEW_TAB), ...MORE_TABS];

/** This member's tabs, in order. */
const tabsOf = (m: Member): Tab[] => [
  ...m.nav.flatMap((v) => (v === 'home' ? [] : [VIEW_TAB[v]])),
  ...MORE_TABS.filter((t) => t.show(m)),
];

/**
 * Keeps --topbar-h on <html> at the header's real height: the full-height pages subtract it.
 * `el` is the header's zero-width .topbar-h, so a new width (the page scrollbar going on or off
 * when the value changes) is not a resize: no "ResizeObserver loop" error.
 */
function measureTopbar(el: HTMLElement) {
  const root = document.documentElement.style;
  const ro = new ResizeObserver(() => root.setProperty('--topbar-h', `${el.offsetHeight}px`));
  ro.observe(el);
  return () => {
    ro.disconnect();
    root.removeProperty('--topbar-h');
  };
}

const under = (path: string, p: string) => path === p || path.startsWith(`${p}/`);

export function Layout() {
  const me = useMe();
  const path = useRouterState({ select: (s) => s.location.pathname });
  // At "/": the page an old "?dest=?view=…" link names (dest.ts). The "/" route takes a signed-in visitor
  // there too, and Layout decides again on that page (Enrol on an applicant page, NoAccess elsewhere).
  const oldPage = useRouterState({
    select: (s) => {
      const dest = s.location.pathname === '/' ? (s.location.search as { dest?: unknown } | undefined)?.dest : undefined;
      return typeof dest === 'string' ? oldDest(dest).page : undefined;
    },
  });
  // At "/": an old "?signout=1" link. The "/" route signs out anyone signed in, on the list or not.
  const oldSignout = useRouterState({
    select: (s) => {
      const v = s.location.pathname === '/' ? (s.location.search as { signout?: unknown } | undefined)?.signout : undefined;
      return v !== undefined && String(v) !== '';
    },
  });
  const member = me.data?.member ? me.data : null;
  // The tab of this page (that page or one under it).
  const here = ALL_TABS.find((t) => under(path.toLowerCase(), t.to)); // the router matches /Apply too
  const isPublic = here?.visitor === 'public';
  // A shared folder link (/files/T-012/<key>): anyone with it, signed in or not, on the list or not. Never Landing,
  // NoAccess (no alert to the administrators) or Enrol; it does not wait for /api/me. No tab: the visitor header.
  const shared = under(path.toLowerCase(), FOLDER_LINK_PAGE); // the router matches /Files/… too
  // The Comments badge is on the tab row: none on a shared folder link, so nothing to ask.
  const unread = useUnread(!shared && !!member && member.nav.includes('comments'));
  const badge = unread.data?.count ?? 0;
  // The tabs scroll sideways under the identity chip: after each page change, the active tab is in view.
  // Again when the Comments badge comes or goes: it moves the tabs after it.
  const tabRow = useRef<HTMLDivElement>(null);
  const signedIn = !!member;
  useEffect(() => {
    const row = tabRow.current;
    const tab = row?.querySelector('a.active');
    if (!row || !tab) return;
    const t = tab.getBoundingClientRect();
    const r = row.getBoundingClientRect();
    if (t.right > r.right) row.scrollLeft += t.right - r.right;
    else if (t.left < r.left) row.scrollLeft -= r.left - t.left;
  }, [path, signedIn, badge]);

  // At "/" a visitor gets the full-screen home page and a member the app: no top bar until we know which.
  if (me.isPending && path === '/') return <p className="muted pad load-wait">Loading…</p>;

  let body: React.ReactNode = null;
  if (shared) body = <Outlet />;
  else if (me.isPending) body = <p className="muted pad">Loading…</p>;
  // A member stays on the page when /api/me fails when asked again (a reconnect, a Users-page save): typed text is kept.
  else if (me.isError && !member) body = <p className="error pad">Could not reach the server: {me.error.message}</p>;
  else if (member || isPublic || (me.data && (oldPage || oldSignout))) body = <Outlet />;
  // Nobody signed in: the public home page, full screen, without the app's top bar. It names the page asked for.
  else if (me.data === null) return <Landing asked={oldPage ?? path} />;
  else if (me.data && !me.data.member) {
    body =
      me.data.applyOpen && me.data.reason === 'unlisted' && here?.visitor === 'applicant' && here.to === path.toLowerCase() ? (
        <Enrol />
      ) : (
        <NoAccess email={me.data.email} reason={me.data.reason} applyOpen={me.data.applyOpen} />
      );
  }

  // A member (or not known yet): the header bar on the pages that had one, then the tab row.
  // Anyone else, and everyone on a shared folder link: the visitor header, no tabs.
  const visitor = shared || (!me.isPending && !member);
  const bar = here?.bar && { page: here.label, hint: here.bar.hint };
  const visitorPage = shared ? 'Shared folder' : here?.visitor ? (here.visitorTitle ?? here.label) : me.data ? 'No access yet' : '';

  return (
    <div className="app">
      <header className="topbar">
        <span className="topbar-h" ref={measureTopbar} />
        {visitor ? (
          <div className="hub-bar">
            <div>
              <h1><Link to="/" className="hub-home">Digital Medicine Research Hub</Link></h1>
              {visitorPage && <div className="sub">{visitorPage}</div>}
            </div>
            <div className="hub-links">
              <a href="/research-programme/">Research Programme ↗</a>
              {me.data ? (
                <>
                  <span className="hub-email">{me.data.email}</span>
                  <a href={SIGN_OUT_URL}>Sign out</a>
                </>
              ) : (
                isPublic && <Link to="/">Sign in</Link>
              )}
              <SiteVersionLabel />
              <ThemeToggle />
            </div>
          </div>
        ) : (
          <>
            {bar && (
              <div className="hub-bar">
                <h1>
                  <Link to="/" className="hub-home" title="Home">Digital Medicine Research Hub</Link>{' '}
                  <span className="hub-page">· {bar.page}</span>
                </h1>
                {bar.hint && <div className="sub">{bar.hint}</div>}
              </div>
            )}
            <nav className="hub-nav" aria-label="Main">
              {/* The tabs scroll sideways; the identity chip stays at the right (phones: the whole row scrolls). */}
              <div className="hub-tabs" ref={tabRow}>
                {member &&
                  tabsOf(member).map((t) => (
                    <Link
                      key={t.to}
                      to={t.to}
                      className={t.also?.some((p) => under(path, p)) ? 'active' : undefined}
                      activeProps={{ className: 'active' }}
                    >
                      <span aria-hidden="true">{t.icon}</span> {t.label}
                      {t.to === '/comments' && badge > 0 && (
                        <span className="nav-n" aria-label={`${badge} new`}>
                          {badge > 99 ? '99+' : badge}
                        </span>
                      )}
                    </Link>
                  ))}
              </div>
              {member && (
                <span className="hub-id" title={`Signed in as ${member.person ? `${member.person}, ` : ''}${member.email} · role: ${rolesText(member)}`}>
                  <ThemeToggle />
                  <span className="hid-nm">{member.person || member.email}</span>
                  <span className="hid-grp">{rolesText(member)}</span>
                  <span className="hid-em">{member.email}</span>
                  <a className="hid-out" href={SIGN_OUT_URL}>Sign out</a>
                  <SiteVersionLabel />
                </span>
              )}
            </nav>
          </>
        )}
        {/* READ_ONLY on the server (the cutover freeze, a rollback): every save is refused with 503. */}
        {me.data?.readOnly && (
          <div className="ro-banner" role="status">
            The Research Hub is read-only for now: you can look around, but nothing can be saved.
          </div>
        )}
        <SessionBar />
        {/* A newer version is on the site than this page runs: Reload. */}
        <NewVersionBar />
      </header>

      <main>{body}</main>
    </div>
  );
}

/** An unlisted visitor opening an applicant page: enrol them, then show it. */
function Enrol() {
  const qc = useQueryClient();
  const [error, setError] = useState('');
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    sendJson('POST', '/api/apply/enrol')
      .then(() => qc.invalidateQueries({ queryKey: ['me'] }))
      .catch((e: Error) => setError(e.message));
  }, [qc]);
  return error ? <p className="error pad">{error}</p> : <p className="muted pad">Setting up your applicant account…</p>;
}
